import { useState } from "react"
import { router } from "@inertiajs/react"
import { CreditCard, ShoppingBag, User, Users } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { ScreenHeader } from "@/components/ui/headers"
import { createUserPaymentAttempt } from "@/services/user-payment-attempts"
import { cancelBillSplit, openBillSplit } from "@/services/user-bill-splits"
import { formatMoney, parseDecimal } from "./money"
import { MetaChip, OrderLine } from "./order-line"
import { PaySheet, type PayConfirmation } from "./pay-sheet"
import type { BillDetailed, BillOrderLine, BillParticipant, ServiceContext } from "./types"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

type CartScreenProps = {
  bill: BillDetailed
  serviceContext?: ServiceContext
}

/**
 * Mount under `<UserLayout fillsViewport>`. The summary holds the bottom edge by
 * sitting outside the scrolling list rather than by `position: sticky`, which
 * needs the page box to be exactly one viewport tall. Without that prop the
 * column has no height to divide and the list never scrolls.
 */

export function CartScreen({ bill, serviceContext = "dine_in" }: CartScreenProps) {
  const isPickup = serviceContext === "pickup"
  const [payOpen, setPayOpen] = useState(false)
  // Minted once per drawer opening; the confirmed payload is folded in below.
  const [paySessionId, setPaySessionId] = useState<string>(generateIdempotencyKey)
  const [paymentErrors, setPaymentErrors] = useState<string[]>([])
  const [paymentNotice, setPaymentNotice] = useState<string | null>(null)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)

  const payableOrders = bill.orders.filter((order) => order.paymentStatus === "unpaid")
  // What the table has consumed so far — no tip, no payment decision attached.
  const consumedTotal = payableOrders.reduce((sum, order) => sum + parseDecimal(order.lineTotal), 0)
  const myItems = payableOrders.filter((order) => order.isMine)

  // Location and table are named by the shell top bar on every screen, so the
  // header here only reports what is specific to this bill.
  const subtitle = t("ui.user.common.products_count", { count: bill.itemCount })

  const openPay = () => {
    setPaySessionId(generateIdempotencyKey())
    setPaymentErrors([])
    setPaymentNotice(null)
    setPayOpen(true)
  }

  const handleConfirmPay = async (payment: PayConfirmation) => {
    setPaymentSubmitting(true)
    setPaymentErrors([])
    setPaymentNotice(null)
    try {
      const result = await createUserPaymentAttempt({
        billId: bill.id,
        orderIds: payment.orderIds,
        paymentMethod: payment.method,
        tipPercentage: payment.tipPercentage,
        tipAmount: payment.tipAmount,
        splitId: payment.splitId,
        returnUrl: new URL(`/bills/${bill.id}`, window.location.origin).toString(),
        idempotencyKey: idempotencyKeyFor(paySessionId, payment),
      })
      if (!result.ok) {
        setPaymentErrors(result.errors)
        return
      }
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl)
        return
      }
      setPaymentNotice(t("ui.react.features.user.pay_sheet.offline_payment_pending"))
      router.reload({ only: ["bill", "cartCount"] })
    } catch {
      setPaymentErrors([t("ui.react.features.user.pay_sheet.network_error")])
    } finally {
      setPaymentSubmitting(false)
    }
  }

  // Opening or cancelling a split changes what every diner at the table sees,
  // so the bill prop is reloaded rather than patched locally.
  const handleOpenSplit = async (payerCount: number) => {
    setPaymentSubmitting(true)
    setPaymentErrors([])
    try {
      const result = await openBillSplit(bill.id, payerCount)
      if (!result.ok) {
        setPaymentErrors(result.errors)
        return
      }
      router.reload({ only: ["bill", "cartCount"] })
    } catch {
      setPaymentErrors([t("ui.react.features.user.pay_sheet.network_error")])
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const handleCancelSplit = async () => {
    setPaymentSubmitting(true)
    setPaymentErrors([])
    try {
      const result = await cancelBillSplit(bill.id)
      if (!result.ok) {
        setPaymentErrors(result.errors)
        return
      }
      router.reload({ only: ["bill", "cartCount"] })
    } catch {
      setPaymentErrors([t("ui.react.features.user.pay_sheet.network_error")])
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const isEmpty = bill.orders.length === 0

  return (
    <div
      className="flex min-h-0 flex-1 flex-col text-foreground"
      data-testid="cart-screen-view"
      data-bill-id={bill.id}
    >
      {/* A tab-bar destination is never "behind" anything, so no back affordance.
          The participants ride in the header: who is on this bill is an attribute
          of it, not a card competing with the items for the top of the screen. */}
      <ScreenHeader
        className="shrink-0"
        title={t("ui.react.features.user.cart_screen.bill")}
        subtitle={subtitle}
        trailing={
          !isPickup && bill.participants.length > 0 ? (
            <ParticipantAvatars participants={bill.participants} />
          ) : undefined
        }
      />

      <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col">
        <ul className="min-h-0 flex-1 overflow-y-auto px-4" data-testid="cart-items-list">
          {isEmpty ? (
            <li className="py-12 text-center text-sm text-muted-foreground" data-testid="cart-empty">
              {t("ui.react.features.user.cart_screen.bill_empty_back_menu")}
            </li>
          ) : (
            bill.orders.map((order) => (
              <CartItemRow key={order.id} order={order} currency={bill.currency} isPickup={isPickup} />
            ))
          )}
        </ul>

        {/* Summary — only what was consumed. Every payment decision lives in the
            drawer. It sits outside the scrolling list, so it holds the bottom edge
            at every breakpoint whether the bill is three lines or thirty: the
            amount owed is the one figure that must never require scrolling to
            find. */}
        <aside className="shrink-0 border-t bg-background py-4" data-testid="cart-summary">
          <div className="mx-4 rounded-2xl border bg-card p-4 shadow-xs">
            <div
              className="flex items-baseline justify-between gap-3"
              data-testid="cart-consumed-total"
            >
              <span className="text-sm text-muted-foreground">
                {t("ui.react.features.user.cart_screen.consumed_total")}
              </span>
              <span data-testid="cart-consumed-value" className="text-lg font-semibold tabular-nums">
                {formatMoney(consumedTotal, { currency: bill.currency })}
              </span>
            </div>

            <Button
              type="button"
              size="lg"
              className="mt-4 w-full"
              data-testid="open-pay-sheet-button"
              onClick={openPay}
              disabled={payableOrders.length === 0}
            >
              <CreditCard />
              {t("ui.react.features.user.pay_sheet.pay")}
            </Button>
          </div>
        </aside>
      </div>

      <PaySheet
        open={payOpen}
        onClose={() => setPayOpen(false)}
        onConfirm={handleConfirmPay}
        onOpenSplit={handleOpenSplit}
        onCancelSplit={handleCancelSplit}
        split={bill.split}
        tableOrders={bill.orders}
        myOrders={myItems}
        participants={bill.participants}
        isPickup={isPickup}
        currency={bill.currency}
        tipPercentages={bill.tipPercentages}
        pickupDinerFeePercentage={bill.pickupDinerFeePercentage}
        enabledPaymentMethods={bill.enabledPaymentMethods}
        isSubmitting={paymentSubmitting}
        errors={paymentErrors}
        notice={paymentNotice}
      />
    </div>
  )
}

/**
 * Who is on this bill, as a stack of initials. Three at most: past that the
 * count says more than another circle would, and the row has a title to fit.
 * The diner's own mark is tinted so they can find themselves at a glance.
 */
const MAX_VISIBLE_PARTICIPANTS = 3

function ParticipantAvatars({ participants }: { participants: BillParticipant[] }) {
  const visible = participants.slice(0, MAX_VISIBLE_PARTICIPANTS)
  const extras = participants.length - visible.length

  return (
    <div
      className="flex shrink-0 items-center"
      data-testid="cart-participants"
      aria-label={participants.map((participant) => participant.name).join(", ")}
    >
      {visible.map((participant) => (
        <Avatar
          key={participant.id}
          className="-ml-2 size-7 border-2 border-background first:ml-0"
          title={participant.name}
        >
          <AvatarFallback
            className={cn(
              "text-xs font-bold",
              participant.isMine && "bg-brand-subtle text-brand-ink"
            )}
          >
            {participant.initials}
          </AvatarFallback>
        </Avatar>
      ))}
      {extras > 0 && (
        <span className="-ml-2 flex size-7 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-bold tabular-nums text-muted-foreground">
          +{extras}
        </span>
      )}
    </div>
  )
}

function CartItemRow({
  order,
  currency,
  isPickup,
}: {
  order: BillOrderLine
  currency: string
  isPickup: boolean
}) {
  const modifiersLabel =
    order.modifiers && order.modifiers.length > 0
      ? order.modifiers.map((modifier) => modifier.name).join(" · ")
      : order.notes || null

  return (
    <OrderLine
      testId={`cart-item-${order.id}`}
      quantity={order.quantity}
      name={order.name}
      detail={modifiersLabel}
      status={order.status}
      statusLabel={order.statusLabel}
      lineTotal={order.lineTotal}
      currency={currency}
      discountAmount={order.discountAmount}
      discountLabel={order.discountLabel}
      // The bill line and the tracking screen are the same order seen twice, so
      // the line is the way into "where is my food" rather than a dead row.
      href={`/orders/${order.id}`}
      chips={
        <>
          {!isPickup && order.serviceType === "take_out" && (
            <MetaChip>
              <ShoppingBag className="size-3" />
              {t("ui.react.features.user.dish_detail.take_out")}
            </MetaChip>
          )}
          {!isPickup && (
            <MetaChip className={order.isMine ? "bg-brand-subtle text-brand-ink" : undefined}>
              {order.isMine ? <User className="size-3" /> : <Users className="size-3" />}
              {order.isMine ? t("ui.user.common.me") : order.whoName}
            </MetaChip>
          )}
        </>
      }
    />
  )
}

// Scope, tip and method all stay editable inside the drawer, and the backend
// replays whatever attempt already owns a key. So the key has to describe the
// intent: retrying the same choice reuses it, changing any choice mints a new one.
function idempotencyKeyFor(sessionId: string, payment: PayConfirmation) {
  return [
    sessionId,
    payment.orderIds.join("."),
    payment.method,
    payment.tipPercentage,
    payment.tipAmount ?? 0,
  ].join("-")
}

function generateIdempotencyKey() {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `pay-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
